'use client';

import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { LiveMessage } from '../../../data/liveMessageData/liveMessage';
import ImageGalleryGrid from '../../shared/ImageGalleryGrid';
import ImagePreviewPopup from '../../shared/ImagePreviewPopup';
import { useTranslations } from 'next-intl';

interface GalleryPopupProps {
  isOpen: boolean;
  onClose: () => void;
  images: LiveMessage[];
  onDeleteMessage?: (messageId: string) => Promise<void>;
  onSetDefaultImage?: (messageId: string) => Promise<void>;
}

export default function GalleryPopup({ 
  isOpen, 
  onClose, 
  images,
  onDeleteMessage,
  onSetDefaultImage
}: GalleryPopupProps) {
  const t = useTranslations('chat');
  const popupRef = useRef<HTMLDivElement>(null);
  
  // Image preview popup state
  const [imagePreviewState, setImagePreviewState] = useState<{
    isOpen: boolean;
    message: LiveMessage | null;
  }>({
    isOpen: false,
    message: null
  });

  // Handle escape key to close
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    const target = event.target as Element;
    
    // Don't close if clicking on the popup content
    if (popupRef.current && popupRef.current.contains(target)) {
      return;
    }
    
    // Close if clicking on the backdrop
    onClose();
  };

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black/20 flex items-end justify-end z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleBackdropClick}
      >
        <div 
          ref={popupRef}
          className={`relative bg-[#1a1a1e] backdrop-blur-md border-l border-white/5 h-full w-full max-w-md transform transition-transform duration-300 ease-in-out flex flex-col ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4">
            <h3 className="text-white text-lg font-semibold">{t('photoAlbum')}</h3>
            <button 
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto dark-scrollbar">
            <ImageGalleryGrid 
              images={images}
              imagesPerRow={2}
              onImageClick={(message) => {
                setImagePreviewState({
                  isOpen: true,
                  message: message
                });
              }}
              onDeleteMessage={onDeleteMessage}
              onSetDefaultImage={onSetDefaultImage}
            />
          </div>
        </div>
      </div>

      {/* Image Preview Popup - contained within gallery */}
      <ImagePreviewPopup
        isOpen={imagePreviewState.isOpen}
        onClose={() => setImagePreviewState(prev => ({ ...prev, isOpen: false }))}
        message={imagePreviewState.message}
      />
    </>
  );
}